import type { PoseLandmark } from '../../../../types';

/**
 * Data structure for a single growing circle in the visualization.
 */
export interface Circle {
  /** Position in normalized coordinates [0, 1] */
  position: { x: number; y: number };
  /** Current size (diameter) in pixels */
  currentSize: number;
  /** Maximum size (diameter) in pixels */
  maxSize: number;
  /** Growth rate in pixels per second */
  growthRate: number;
  /** Age in seconds since circle was created */
  age: number;
  /** Growth duration in seconds */
  growthDuration: number;
  /** Relative position within parent shape (for head/neck/torso circles) */
  relativePosition?: { x: number; y: number };
}

/**
 * Limb segment definition for circle placement.
 */
export interface LimbSegment {
  start: PoseLandmark;
  end: PoseLandmark;
  name: string;
}

/**
 * Tunable parameters shared across the growing-circles generators.
 * Extracted so generators are pure and independently testable.
 */
export interface CircleGenConfig {
  /** Base max size (diameter) in pixels at low motion */
  baseCircleSize: number;
  minGrowthDuration: number;
  maxGrowthDuration: number;
  /** ±fraction variation applied to growth rate */
  growthRateVariation: number;
  /** Circles start at this fraction of max size */
  startSizeFactor: number;
  /** ±fraction randomness for torso grid positions */
  torsoGridRandomness: number;
}

/**
 * Default circle generation configuration.
 */
export const DEFAULT_CIRCLE_CONFIG: CircleGenConfig = {
  baseCircleSize: 40,
  minGrowthDuration: 0.5,
  maxGrowthDuration: 1.5,
  growthRateVariation: 0.2,
  startSizeFactor: 0.1,
  torsoGridRandomness: 0.15,
};
